'use client';

import { createTheme, responsiveFontSizes, type Theme } from '@mui/material/styles';

type ChattengerMuiMode = 'light' | 'dark';

const isTestEnvironment = process.env.NODE_ENV === 'test';

const THEME_COLOR_TOKENS: Record<
    ChattengerMuiMode,
    {
        primary: string;
        primaryContrast: string;
        secondary: string;
        error: string;
        warning: string;
        backgroundDefault: string;
        backgroundPaper: string;
        textPrimary: string;
        textSecondary: string;
        divider: string;
    }
> = {
    dark: {
        primary: '#c4d1e5',
        primaryContrast: '#10151b',
        secondary: '#f4f7fc',
        error: '#ff7769',
        warning: '#ffc28a',
        backgroundDefault: '#0f141a',
        backgroundPaper: '#171d24',
        textPrimary: '#edf2fb',
        textSecondary: 'rgba(211, 220, 235, 0.78)',
        divider: 'rgba(236, 243, 255, 0.09)',
    },
    light: {
        primary: '#314056',
        primaryContrast: '#f7fbff',
        secondary: '#253246',
        error: '#d34e42',
        warning: '#c28a33',
        backgroundDefault: '#f4f6fb',
        backgroundPaper: '#fcfdff',
        textPrimary: '#16202d',
        textSecondary: 'rgba(49, 63, 84, 0.78)',
        divider: 'rgba(27, 46, 79, 0.1)',
    },
};

const buildMaterialSurface = (options?: { borderRadius?: string; boxShadow?: string }) =>
    ({
        backgroundImage:
            'linear-gradient(180deg, color-mix(in srgb, var(--text-primary) 4%, transparent), color-mix(in srgb, var(--text-primary) 1.5%, transparent))',
        backgroundColor: 'var(--panel-solid)',
        border: '1px solid var(--panel-border)',
        borderRadius: options?.borderRadius ?? 'var(--radius-lg)',
        boxShadow: options?.boxShadow ?? 'var(--panel-shadow)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        transition: [
            'background-color var(--motion-duration-medium) var(--motion-ease-standard)',
            'border-color var(--motion-duration-medium) var(--motion-ease-standard)',
            'box-shadow var(--motion-duration-medium) var(--motion-ease-standard)',
            'transform var(--motion-duration-fast) var(--motion-ease-standard)',
        ].join(', '),
    }) as const;

const buildComponentOverrides = (): Theme['components'] => ({
    ...(isTestEnvironment
        ? {
              MuiButtonBase: {
                  defaultProps: {
                      disableRipple: true,
                      disableTouchRipple: true,
                      focusRipple: false,
                  },
              },
          }
        : {}),
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                scrollbarColor: 'var(--surface-3) transparent',
            },
            '::selection': {
                backgroundColor: 'color-mix(in srgb, var(--accent) 22%, transparent)',
                color: 'var(--text-primary)',
            },
            '::-webkit-scrollbar': {
                width: '0.75rem',
                height: '0.75rem',
            },
            '::-webkit-scrollbar-thumb': {
                borderRadius: '999px',
                backgroundColor: 'var(--surface-2)',
                border: '2px solid transparent',
                backgroundClip: 'padding-box',
            },
            '::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '*, *::before, *::after': {
                    animationDuration: '0.01ms !important',
                    animationIterationCount: '1 !important',
                    scrollBehavior: 'auto !important',
                    transitionDuration: '0.01ms !important',
                },
            },
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: buildMaterialSurface(),
        },
    },
    MuiCard: {
        styleOverrides: {
            root: {
                ...buildMaterialSurface(),
                overflow: 'hidden',
            },
        },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                ...buildMaterialSurface({ borderRadius: '1.5rem 0 0 1.5rem' }),
                boxShadow: '0 24px 54px color-mix(in srgb, var(--shell-pane-bg) 24%, transparent)',
            },
        },
    },
    MuiPopover: {
        styleOverrides: {
            paper: {
                ...buildMaterialSurface(),
                boxShadow: '0 20px 38px color-mix(in srgb, var(--shell-pane-bg) 20%, transparent)',
            },
        },
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                ...buildMaterialSurface(),
                boxShadow: '0 20px 38px color-mix(in srgb, var(--shell-pane-bg) 20%, transparent)',
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                ...buildMaterialSurface({ borderRadius: 'var(--radius-xl)' }),
                boxShadow: '0 28px 60px color-mix(in srgb, var(--shell-pane-bg) 26%, transparent)',
            },
        },
    },
    MuiBackdrop: {
        styleOverrides: {
            root: {
                backgroundColor: 'var(--overlay-scrim)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                transition: [
                    'background-color var(--motion-duration-medium) var(--motion-ease-standard)',
                    'backdrop-filter var(--motion-duration-medium) var(--motion-ease-standard)',
                ].join(', '),
            },
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                minHeight: '2.9rem',
                borderRadius: 'var(--radius-md)',
                paddingInline: '1.18rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                textTransform: 'none',
                transition: [
                    'transform var(--motion-duration-fast) var(--motion-ease-standard)',
                    'box-shadow var(--motion-duration-medium) var(--motion-ease-standard)',
                    'background-color var(--motion-duration-medium) var(--motion-ease-standard)',
                    'border-color var(--motion-duration-medium) var(--motion-ease-standard)',
                    'color var(--motion-duration-medium) var(--motion-ease-standard)',
                ].join(', '),
                '&:hover': {
                    transform: 'translateY(-1px)',
                },
                '&:active': {
                    transform: 'scale(0.985)',
                },
                '&.Mui-focusVisible': {
                    boxShadow: '0 0 0 4px var(--focus-ring)',
                },
            },
            sizeSmall: {
                minHeight: '2.45rem',
                paddingInline: '0.95rem',
                fontSize: '0.88rem',
            },
            containedPrimary: {
                color: 'var(--accent-contrast)',
                background:
                    'linear-gradient(180deg, color-mix(in srgb, var(--accent-strong) 92%, var(--text-primary) 8%), color-mix(in srgb, var(--accent) 88%, var(--accent-contrast) 12%))',
                boxShadow: '0 14px 28px color-mix(in srgb, var(--accent) 26%, transparent)',
                '&:hover': {
                    background:
                        'linear-gradient(180deg, color-mix(in srgb, var(--accent-strong) 96%, var(--text-primary) 4%), color-mix(in srgb, var(--accent) 92%, var(--accent-contrast) 8%))',
                    boxShadow: '0 18px 32px color-mix(in srgb, var(--accent) 32%, transparent)',
                },
            },
            outlinedPrimary: {
                color: 'var(--text-primary)',
                borderColor: 'var(--accent-border)',
                backgroundColor: 'var(--surface-0)',
                boxShadow: '0 6px 18px color-mix(in srgb, var(--shell-pane-bg) 14%, transparent)',
                '&:hover': {
                    borderColor: 'color-mix(in srgb, var(--accent-border) 84%, var(--text-primary) 16%)',
                    backgroundColor: 'var(--surface-1)',
                },
            },
            textPrimary: {
                color: 'var(--text-primary)',
                '&:hover': {
                    backgroundColor: 'var(--surface-1)',
                },
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: {
                width: '2.85rem',
                height: '2.85rem',
                border: '1px solid var(--panel-border)',
                borderRadius: '1rem',
                backgroundColor: 'var(--surface-0)',
                color: 'var(--text-primary)',
                boxShadow: '0 8px 18px color-mix(in srgb, var(--shell-pane-bg) 12%, transparent)',
                transition: [
                    'transform var(--motion-duration-fast) var(--motion-ease-standard)',
                    'box-shadow var(--motion-duration-medium) var(--motion-ease-standard)',
                    'background-color var(--motion-duration-medium) var(--motion-ease-standard)',
                    'border-color var(--motion-duration-medium) var(--motion-ease-standard)',
                ].join(', '),
                '&:hover': {
                    backgroundColor: 'var(--surface-1)',
                    borderColor: 'color-mix(in srgb, var(--accent-border) 72%, var(--panel-border))',
                    boxShadow: '0 12px 22px color-mix(in srgb, var(--shell-pane-bg) 14%, transparent)',
                    transform: 'translateY(-1px)',
                },
                '&:active': {
                    transform: 'scale(0.97)',
                },
                '&.Mui-focusVisible': {
                    boxShadow: '0 0 0 4px var(--focus-ring)',
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                minHeight: '3.5rem',
                alignItems: 'center',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--surface-0)',
                transition: [
                    'background-color var(--motion-duration-medium) var(--motion-ease-standard)',
                    'box-shadow var(--motion-duration-medium) var(--motion-ease-standard)',
                    'border-color var(--motion-duration-medium) var(--motion-ease-standard)',
                ].join(', '),
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--surface-2)',
                    transition: 'border-color var(--motion-duration-medium) var(--motion-ease-standard)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--surface-3)',
                },
                '&.Mui-focused': {
                    backgroundColor: 'color-mix(in srgb, var(--panel-solid) 96%, var(--text-primary) 4%)',
                    boxShadow: '0 0 0 4px var(--focus-ring)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--accent-border)',
                },
            },
            input: {
                color: 'var(--text-primary)',
            },
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                color: 'var(--text-secondary)',
                '&.Mui-focused': {
                    color: 'var(--text-primary)',
                },
            },
        },
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: {
                marginLeft: 0,
                color: 'var(--text-muted)',
            },
        },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: '999px',
                border: '1px solid var(--surface-2)',
                backgroundColor: 'color-mix(in srgb, var(--surface-1) 92%, transparent)',
                color: 'var(--text-primary)',
            },
            label: {
                fontSize: '0.78rem',
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: '0.02em',
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 16px 34px color-mix(in srgb, var(--shell-pane-bg) 18%, transparent)',
            },
        },
    },
});

export const createChattengerMuiTheme = (mode: ChattengerMuiMode) => {
    const paletteTokens = THEME_COLOR_TOKENS[mode];

    return responsiveFontSizes(
        createTheme({
            palette: {
                mode,
                primary: {
                    main: paletteTokens.primary,
                    contrastText: paletteTokens.primaryContrast,
                },
                secondary: {
                    main: paletteTokens.secondary,
                },
                error: {
                    main: paletteTokens.error,
                },
                warning: {
                    main: paletteTokens.warning,
                },
                background: {
                    default: paletteTokens.backgroundDefault,
                    paper: paletteTokens.backgroundPaper,
                },
                text: {
                    primary: paletteTokens.textPrimary,
                    secondary: paletteTokens.textSecondary,
                },
                divider: paletteTokens.divider,
            },
            shape: {
                borderRadius: 22,
            },
            typography: {
                fontFamily: "var(--font-display), 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                button: {
                    textTransform: 'none',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                },
                h1: {
                    fontWeight: 700,
                    letterSpacing: '-0.05em',
                    lineHeight: 1.05,
                },
                h2: {
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    lineHeight: 1.08,
                },
                h3: {
                    fontWeight: 600,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.12,
                },
                body1: {
                    lineHeight: 1.5,
                },
                body2: {
                    lineHeight: 1.45,
                },
            },
            components: buildComponentOverrides(),
        })
    );
};
